// src/rentals-api-v3/models/Address.ts
function AddressFromJSON(json) {
  return AddressFromJSONTyped(json, false);
}
function AddressFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    street: json["street"] == null ? void 0 : json["street"],
    state: json["state"] == null ? void 0 : json["state"],
    city: json["city"] == null ? void 0 : json["city"],
    country: json["country"] == null ? void 0 : json["country"],
    postCode: json["postCode"] == null ? void 0 : json["postCode"]
  };
}

// src/rentals-api-v3/models/CurrencyValue.ts
function CurrencyValueFromJSON(json) {
  return CurrencyValueFromJSONTyped(json, false);
}
function CurrencyValueFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["currencyCode"],
    value: json["value"]
  };
}

// src/rentals-api-v3/runtime.ts
import { formatISO } from "date-fns";
var BASE_PATH = "https://rentals.api.jucy.cloud".replace(/\/+$/, "");
var Configuration = class {
  constructor(configuration = {}) {
    this.configuration = configuration;
  }
  set config(configuration) {
    this.configuration = configuration;
  }
  get basePath() {
    return this.configuration.basePath != null ? this.configuration.basePath : BASE_PATH;
  }
  get fetchApi() {
    return this.configuration.fetchApi || fetch;
  }
  get middleware() {
    return this.configuration.middleware || [];
  }
  get queryParamsStringify() {
    return this.configuration.queryParamsStringify || querystring;
  }
  get username() {
    return this.configuration.username;
  }
  get password() {
    return this.configuration.password;
  }
  get apiKey() {
    const apiKey = this.configuration.apiKey;
    if (apiKey) {
      return typeof apiKey === "function" ? apiKey : () => apiKey;
    }
    return void 0;
  }
  get accessToken() {
    const accessToken = this.configuration.accessToken;
    if (accessToken) {
      return typeof accessToken === "function" ? accessToken : async () => accessToken;
    }
    return void 0;
  }
  get headers() {
    return this.configuration.headers;
  }
  get credentials() {
    return this.configuration.credentials;
  }
};
var DefaultConfig = new Configuration();
async function createResponseErrorMessage(response, fallback = "Response returned an error code") {
  try {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.startsWith("application/json")) {
      const json = await response.json();
      return new ResponseError(response, json?.message || json?.Message || response.statusText || fallback, json);
    }
    const body = await response.text();
    return new ResponseError(response, body || response.statusText || fallback, { body });
  } catch (e) {
    return new ResponseError(response, fallback, { error: e });
  }
}
var BaseAPI = class _BaseAPI {
  constructor(configuration = DefaultConfig) {
    this.configuration = configuration;
    this.middleware = configuration.middleware;
  }
  static jsonRegex = new RegExp("^(:?application/json|[^;/ 	]+/[^;/ 	]+[+]json)[ 	]*(:?;.*)?$", "i");
  middleware;
  withMiddleware(...middlewares) {
    const next = this.clone();
    next.middleware = next.middleware.concat(...middlewares);
    return next;
  }
  withPreMiddleware(...preMiddlewares) {
    const middlewares = preMiddlewares.map((pre) => ({ pre }));
    return this.withMiddleware(...middlewares);
  }
  withPostMiddleware(...postMiddlewares) {
    const middlewares = postMiddlewares.map((post) => ({ post }));
    return this.withMiddleware(...middlewares);
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    if (!mime) {
      return false;
    }
    return _BaseAPI.jsonRegex.test(mime);
  }
  async request(context, initOverrides) {
    const { url, init } = await this.createFetchParams(context, initOverrides);
    const response = await this.fetchApi(url, init);
    if (response && response.status >= 200 && response.status < 300) {
      return response;
    }
    throw await createResponseErrorMessage(response, "Response returned an error code");
  }
  async createFetchParams(context, initOverrides) {
    let url = this.configuration.basePath + context.path;
    if (context.query !== void 0 && Object.keys(context.query).length !== 0) {
      url += "?" + this.configuration.queryParamsStringify(context.query);
    }
    const headers = Object.assign({}, this.configuration.headers, context.headers);
    Object.keys(headers).forEach((key) => headers[key] === void 0 ? delete headers[key] : {});
    const initOverrideFn = typeof initOverrides === "function" ? initOverrides : async () => initOverrides;
    const initParams = {
      method: context.method,
      headers,
      body: context.body,
      credentials: this.configuration.credentials
    };
    const overriddenInit = {
      ...initParams,
      ...await initOverrideFn({
        init: initParams,
        context
      })
    };
    let body;
    if (isFormData(overriddenInit.body) || overriddenInit.body instanceof URLSearchParams || isBlob(overriddenInit.body)) {
      body = overriddenInit.body;
    } else if (this.isJsonMime(headers["Content-Type"])) {
      body = JSON.stringify(overriddenInit.body);
    } else {
      body = overriddenInit.body;
    }
    const init = {
      ...overriddenInit,
      body
    };
    return { url, init };
  }
  fetchApi = async (url, init) => {
    let fetchParams = { url, init };
    for (const middleware of this.middleware) {
      if (middleware.pre) {
        fetchParams = await middleware.pre({
          fetch: this.fetchApi,
          ...fetchParams
        }) || fetchParams;
      }
    }
    let response = void 0;
    try {
      response = await (this.configuration.fetchApi || fetch)(fetchParams.url, fetchParams.init);
    } catch (e) {
      for (const middleware of this.middleware) {
        if (middleware.onError) {
          response = await middleware.onError({
            fetch: this.fetchApi,
            url: fetchParams.url,
            init: fetchParams.init,
            error: e,
            response: response ? response.clone() : void 0
          }) || response;
        }
      }
      if (response === void 0) {
        if (e instanceof Error) {
          throw new FetchError(e, "The request failed and the interceptors did not return an alternative response");
        } else {
          throw e;
        }
      }
    }
    for (const middleware of this.middleware) {
      if (middleware.post) {
        response = await middleware.post({
          fetch: this.fetchApi,
          url: fetchParams.url,
          init: fetchParams.init,
          response: response.clone()
        }) || response;
      }
    }
    return response;
  };
  /**
   * Create a shallow clone of `this` by constructing a new instance
   * and then shallow cloning data members.
   */
  clone() {
    const constructor = this.constructor;
    const next = new constructor(this.configuration);
    next.middleware = this.middleware.slice();
    return next;
  }
};
function isBlob(value) {
  return typeof Blob !== "undefined" && value instanceof Blob;
}
function isFormData(value) {
  return typeof FormData !== "undefined" && value instanceof FormData;
}
function stringToDate(value) {
  return value ? new Date(value.replace(/Z$/, "")) : /* @__PURE__ */ new Date("invalid");
}
var ResponseError = class extends Error {
  constructor(response, msg, data) {
    super(msg);
    this.response = response;
    this.data = data;
  }
  name = "ResponseError";
  data;
};
var FetchError = class extends Error {
  constructor(cause, msg) {
    super(msg);
    this.cause = cause;
  }
  name = "FetchError";
};
var RequiredError = class extends Error {
  constructor(field, msg) {
    super(msg);
    this.field = field;
  }
  name = "RequiredError";
};
function querystring(params, prefix = "") {
  return Object.keys(params).map((key) => querystringSingleKey(key, params[key], prefix)).filter((part) => part.length > 0).join("&");
}
function querystringSingleKey(key, value, keyPrefix = "") {
  const fullKey = keyPrefix + (keyPrefix.length ? `[${key}]` : key);
  if (value instanceof Array) {
    const multiValue = value.map((singleValue) => encodeURIComponent(String(singleValue))).join(`&${encodeURIComponent(fullKey)}=`);
    return `${encodeURIComponent(fullKey)}=${multiValue}`;
  }
  if (value instanceof Set) {
    const valueAsArray = Array.from(value);
    return querystringSingleKey(key, valueAsArray, keyPrefix);
  }
  if (value instanceof Date) {
    return `${encodeURIComponent(fullKey)}=${encodeURIComponent(formatISO(value))}`;
  }
  if (value instanceof Object) {
    return querystring(value, fullKey);
  }
  return `${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`;
}
var JSONApiResponse = class {
  constructor(raw, transformer = (jsonValue) => jsonValue) {
    this.raw = raw;
    this.transformer = transformer;
  }
  async value() {
    return this.transformer(await this.raw.json());
  }
};

// src/rentals-api-v3/models/MandatoryProduct.ts
function MandatoryProductFromJSON(json) {
  return MandatoryProductFromJSONTyped(json, false);
}
function MandatoryProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: json["category"],
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    qty: json["qty"],
    total: CurrencyValueFromJSON(json["total"]),
    isExcluded: json["isExcluded"] == null ? void 0 : json["isExcluded"]
  };
}

// src/rentals-api-v3/models/FleetCategory.ts
function FleetCategoryFromJSON(json) {
  return FleetCategoryFromJSONTyped(json, false);
}
function FleetCategoryFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"],
    description: json["description"],
    code: json["code"],
    categoryCode: json["categoryCode"],
    campaignCode: json["campaignCode"] == null ? void 0 : json["campaignCode"],
    campaignId: json["campaignId"] == null ? void 0 : json["campaignId"],
    id: json["id"],
    type: json["type"],
    rentalDays: json["rentalDays"],
    dealText: json["dealText"] == null ? void 0 : json["dealText"],
    freeDays: json["freeDays"] == null ? void 0 : json["freeDays"],
    fleetTypeCode: json["fleetTypeCode"],
    availabilityMessage: json["availabilityMessage"],
    dailyRate: CurrencyValueFromJSON(json["dailyRate"]),
    total: CurrencyValueFromJSON(json["total"]),
    discountPercentageApplied: json["discountPercentageApplied"] == null ? void 0 : json["discountPercentageApplied"],
    mandatoryFees: json["mandatoryFees"] == null ? void 0 : json["mandatoryFees"].map(MandatoryProductFromJSON),
    minimumHirePeriod: json["minimumHirePeriod"] == null ? void 0 : json["minimumHirePeriod"],
    isAfterHourPickUp: json["isAfterHourPickUp"] == null ? void 0 : json["isAfterHourPickUp"],
    availability: json["availability"]
  };
}

// src/rentals-api-v3/models/InsuranceType.ts
function InsuranceTypeFromJSON(json) {
  return InsuranceTypeFromJSONTyped(json, false);
}
function InsuranceTypeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/BookingLinesInner.ts
function BookingLinesInnerFromJSON(json) {
  return BookingLinesInnerFromJSONTyped(json, false);
}
function BookingLinesInnerFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: json["category"],
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    qty: json["qty"],
    total: CurrencyValueFromJSON(json["total"]),
    isExcluded: json["isExcluded"] == null ? void 0 : json["isExcluded"],
    excessType: InsuranceTypeFromJSON(json["excessType"]),
    excess: CurrencyValueFromJSON(json["excess"]),
    bond: CurrencyValueFromJSON(json["bond"])
  };
}

// src/rentals-api-v3/models/BookingPaymentInfo.ts
function BookingPaymentInfoFromJSON(json) {
  return BookingPaymentInfoFromJSONTyped(json, false);
}
function BookingPaymentInfoFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    amount: CurrencyValueFromJSON(json["amount"]),
    date: stringToDate(json["date"]),
    type: json["type"],
    merchantFeeName: json["merchantFeeName"] == null ? void 0 : json["merchantFeeName"],
    merchantFeeAmount: json["merchantFeeAmount"] == null ? void 0 : CurrencyValueFromJSON(json["merchantFeeAmount"]),
    total: json["total"] == null ? void 0 : CurrencyValueFromJSON(json["total"])
  };
}

// src/rentals-api-v3/models/BookingStatus.ts
function BookingStatusFromJSON(json) {
  return BookingStatusFromJSONTyped(json, false);
}
function BookingStatusFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/DriversLicence.ts
function DriversLicenceFromJSON(json) {
  return DriversLicenceFromJSONTyped(json, false);
}
function DriversLicenceFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    expiry: json["expiry"] == null ? void 0 : stringToDate(json["expiry"]),
    country: json["country"],
    number: json["number"] == null ? void 0 : json["number"]
  };
}

// src/rentals-api-v3/models/HirerDetails.ts
function HirerDetailsFromJSON(json) {
  return HirerDetailsFromJSONTyped(json, false);
}
function HirerDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    firstName: json["firstName"],
    lastName: json["lastName"],
    email: json["email"],
    mobileNumber: json["mobileNumber"],
    address: json["address"] == null ? void 0 : AddressFromJSON(json["address"]),
    dateOfBirth: json["dateOfBirth"] == null ? void 0 : stringToDate(json["dateOfBirth"]),
    driversLicence: json["driversLicence"] == null ? void 0 : DriversLicenceFromJSON(json["driversLicence"]),
    lucyClubNumber: json["lucyClubNumber"] == null ? void 0 : json["lucyClubNumber"],
    mailingList: json["mailingList"] == null ? void 0 : json["mailingList"],
    arrivalFlightNumber: json["arrivalFlightNumber"] == null ? void 0 : json["arrivalFlightNumber"],
    departureFlightNumber: json["departureFlightNumber"] == null ? void 0 : json["departureFlightNumber"],
    numberOfPeople: json["numberOfPeople"] == null ? void 0 : json["numberOfPeople"],
    loyalty: json["loyalty"] == null ? void 0 : json["loyalty"]
  };
}

// src/rentals-api-v3/models/Booking.ts
function BookingFromJSON(json) {
  return BookingFromJSONTyped(json, false);
}
function BookingFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    quoteId: json["quoteId"],
    reservationId: json["reservationId"],
    reservationReference: json["reservationReference"],
    agentBalanceDue: json["agentBalanceDue"] == null ? void 0 : CurrencyValueFromJSON(json["agentBalanceDue"]),
    agentCollects: json["agentCollects"] == null ? void 0 : CurrencyValueFromJSON(json["agentCollects"]),
    agentCommission: json["agentCommission"] == null ? void 0 : CurrencyValueFromJSON(json["agentCommission"]),
    voucherReference: json["voucherReference"] == null ? void 0 : json["voucherReference"],
    agentName: json["agentName"] == null ? void 0 : json["agentName"],
    balanceDue: json["balanceDue"] == null ? void 0 : CurrencyValueFromJSON(json["balanceDue"]),
    payments: json["payments"] == null ? void 0 : json["payments"].map(BookingPaymentInfoFromJSON),
    status: BookingStatusFromJSON(json["status"]),
    pickUpLocation: json["pickUpLocation"],
    dropOffLocation: json["dropOffLocation"],
    dateCreated: stringToDate(json["dateCreated"]),
    pickUpDate: stringToDate(json["pickUpDate"]),
    dropOffDate: stringToDate(json["dropOffDate"]),
    rentalDays: json["rentalDays"],
    totalPrice: CurrencyValueFromJSON(json["totalPrice"]),
    driverAge: json["driverAge"],
    lines: json["lines"] == null ? void 0 : json["lines"].map(BookingLinesInnerFromJSON),
    hirerDetails: json["hirerDetails"] == null ? void 0 : HirerDetailsFromJSON(json["hirerDetails"]),
    fleetCategory: FleetCategoryFromJSON(json["fleetCategory"]),
    referrerCode: json["referrerCode"] == null ? void 0 : json["referrerCode"]
  };
}

// src/rentals-api-v3/models/CreateBookingResponseStatus.ts
function CreateBookingResponseStatusFromJSON(json) {
  return CreateBookingResponseStatusFromJSONTyped(json, false);
}
function CreateBookingResponseStatusFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/ConvertQuoteResponse.ts
function ConvertQuoteResponseFromJSON(json) {
  return ConvertQuoteResponseFromJSONTyped(json, false);
}
function ConvertQuoteResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    paymentUrl: json["paymentUrl"] == null ? void 0 : json["paymentUrl"],
    status: CreateBookingResponseStatusFromJSON(json["status"]),
    reference: json["reference"],
    bookingNo: json["bookingNo"]
  };
}

// src/rentals-api-v3/models/HirerDetailsSummary.ts
function HirerDetailsSummaryToJSON(value) {
  if (value == null) {
    return value;
  }
  return {
    firstName: value["firstName"],
    lastName: value["lastName"],
    mobileNumber: value["mobileNumber"],
    email: value["email"],
    driversLicenceCountry: value["driversLicenceCountry"],
    acceptedTerms: value["acceptedTerms"],
    mailingList: value["mailingList"],
    loyalty: value["loyalty"]
  };
}

// src/rentals-api-v3/models/CreateBookingResponse.ts
function CreateBookingResponseFromJSON(json) {
  return CreateBookingResponseFromJSONTyped(json, false);
}
function CreateBookingResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    booking: BookingFromJSON(json["booking"]),
    status: CreateBookingResponseStatusFromJSON(json["status"]),
    convertUrl: json["convertUrl"] == null ? void 0 : json["convertUrl"],
    reference: json["reference"],
    bookingNo: json["bookingNo"],
    paymentToken: json["paymentToken"] == null ? void 0 : json["paymentToken"]
  };
}

// src/rentals-api-v3/models/EmailQuoteRequest.ts
function EmailQuoteRequestToJSON(value) {
  if (value == null) {
    return value;
  }
  return {
    pickUpLocation: value["pickUpLocation"],
    dropOffLocation: value["dropOffLocation"],
    pickUpDate: value["pickUpDate"],
    dropOffDate: value["dropOffDate"],
    fleetCategory: value["fleetCategory"],
    driverAge: value["driverAge"],
    numberOfPeople: value["numberOfPeople"],
    campaignCode: value["campaignCode"],
    pascal: value["pascal"],
    insurance: value["insurance"],
    secondaryProducts: value["secondaryProducts"],
    hirerDetails: HirerDetailsSummaryToJSON(value["hirerDetails"]),
    voucherReference: value["voucherReference"],
    reservationReference: value["reservationReference"]
  };
}

// src/rentals-api-v3/models/UpdateReservationRequest.ts
function UpdateReservationRequestToJSON(value) {
  if (value == null) {
    return value;
  }
  return {
    pickUpLocation: value["pickUpLocation"],
    dropOffLocation: value["dropOffLocation"],
    pickUpDate: value["pickUpDate"],
    dropOffDate: value["dropOffDate"],
    fleetCategory: value["fleetCategory"],
    driverAge: value["driverAge"],
    numberOfPeople: value["numberOfPeople"],
    campaignCode: value["campaignCode"],
    pascal: value["pascal"],
    insurance: value["insurance"],
    secondaryProducts: value["secondaryProducts"],
    hirerDetails: HirerDetailsSummaryToJSON(value["hirerDetails"]),
    voucherReference: value["voucherReference"],
    gateway: value["gateway"],
    payment: value["payment"]
  };
}

// src/rentals-api-v3/apis/QuotesApi.ts
var QuotesApi = class extends BaseAPI {
  /**
   * Convert a quote in to a reservation
   */
  async convertQuoteRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling convertQuote().'
      );
    }
    const queryParameters = {};
    if (requestParameters["gateway"] != null) {
      queryParameters["gateway"] = requestParameters["gateway"];
    }
    if (requestParameters["returnUrl"] != null) {
      queryParameters["returnUrl"] = requestParameters["returnUrl"];
    }
    if (requestParameters["failedUrl"] != null) {
      queryParameters["failedUrl"] = requestParameters["failedUrl"];
    }
    if (requestParameters["type"] != null) {
      queryParameters["type"] = requestParameters["type"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request(
      {
        path: `/api/v3/quotes/{reservationRef}/convert`.replace(
          `{${"reservationRef"}}`,
          encodeURIComponent(String(requestParameters["reservationRef"]))
        ),
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => ConvertQuoteResponseFromJSON(jsonValue));
  }
  /**
   * Convert a quote in to a reservation
   */
  async convertQuote(reservationRef, gateway, returnUrl, failedUrl, type, initOverrides) {
    const response = await this.convertQuoteRaw(
      { reservationRef, gateway, returnUrl, failedUrl, type },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Create a new quote and email it to the hirer
   */
  async emailQuoteRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request(
      {
        path: `/api/v3/quotes`,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: EmailQuoteRequestToJSON(requestParameters["emailQuoteRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CreateBookingResponseFromJSON(jsonValue));
  }
  /**
   * Create a new quote and email it to the hirer
   */
  async emailQuote(emailQuoteRequest, initOverrides) {
    const response = await this.emailQuoteRaw({ emailQuoteRequest }, initOverrides);
    return await response.value();
  }
  /**
   * Make updates and edit an existing quote
   */
  async updateQuoteRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling updateQuote().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request(
      {
        path: `/api/v3/quotes/{reservationRef}/update`.replace(
          `{${"reservationRef"}}`,
          encodeURIComponent(String(requestParameters["reservationRef"]))
        ),
        method: "PATCH",
        headers: headerParameters,
        query: queryParameters,
        body: UpdateReservationRequestToJSON(requestParameters["updateReservationRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CreateBookingResponseFromJSON(jsonValue));
  }
  /**
   * Make updates and edit an existing quote
   */
  async updateQuote(reservationRef, updateReservationRequest, initOverrides) {
    const response = await this.updateQuoteRaw(
      { reservationRef, updateReservationRequest },
      initOverrides
    );
    return await response.value();
  }
};
export {
  QuotesApi
};
//# sourceMappingURL=QuotesApi.mjs.map